import { supabase } from '../../config/supabaseClient';

// =====================================================
// TYPES & INTERFACES
// =====================================================

export interface InventoryItem {
  id: string;
  organization_id: string;
  catalog_item_id?: string;
  supplier_id?: string;
  sku?: string;
  current_stock: number;
  reorder_point: number;
  reorder_quantity: number;
  max_stock_level?: number;
  location: string;
  unit_of_measure: string;
  last_counted_at?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
  // Joined data
  catalog_item?: any;
  supplier?: any;
}

export interface InventoryItemInput {
  organization_id: string;
  catalog_item_id?: string;
  supplier_id?: string;
  sku?: string;
  current_stock?: number;
  reorder_point?: number;
  reorder_quantity?: number;
  max_stock_level?: number;
  location?: string;
  unit_of_measure?: string;
  last_counted_at?: string;
  notes?: string;
}

export interface InventoryTransaction {
  id: string;
  organization_id: string;
  inventory_item_id: string;
  transaction_type: 'in' | 'out' | 'adjustment' | 'transfer';
  quantity: number;
  unit_cost?: number;
  reference_type?: string;
  reference_id?: string;
  from_location?: string;
  to_location?: string;
  performed_by?: string;
  notes?: string;
  created_at: string;
  // Joined data
  inventory_item?: InventoryItem;
}

export interface InventoryTransactionInput {
  organization_id: string;
  inventory_item_id: string;
  transaction_type: 'in' | 'out' | 'adjustment' | 'transfer';
  quantity: number;
  unit_cost?: number;
  reference_type?: string;
  reference_id?: string;
  from_location?: string;
  to_location?: string;
  notes?: string;
}

export interface InventoryAlert {
  id: string;
  organization_id: string;
  inventory_item_id: string;
  alert_type: 'low_stock' | 'out_of_stock' | 'overstock';
  alert_message?: string;
  acknowledged: boolean;
  acknowledged_by?: string;
  acknowledged_at?: string;
  created_at: string;
  // Joined data
  inventory_item?: InventoryItem;
}

export interface ListInventoryParams {
  organization_id: string;
  search?: string;
  location?: string;
  low_stock_only?: boolean;
  limit?: number;
  offset?: number;
}

// =====================================================
// HELPER: Get Auth Token
// =====================================================

async function getToken(): Promise<string> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated');
  return session.access_token;
}

// =====================================================
// API BASE URL
// =====================================================

const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? 'https://api.explanatum.co.uk/api/supplier-crm/inventory'
  : 'http://localhost:5000/api/supplier-crm/inventory';

// =====================================================
// INVENTORY ITEMS SERVICE
// =====================================================

export const inventoryService = {
  /**
   * List inventory items with optional filters
   */
  async list(params: ListInventoryParams): Promise<{ data: InventoryItem[]; error: any }> {
    try {
      const token = await getToken();
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, String(value));
        }
      });

      const response = await fetch(`${API_BASE_URL}?${queryParams.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        return { data: [], error: errorData.error || 'Failed to fetch inventory items' };
      }

      const result = await response.json();
      return { data: result.data || [], error: null };
    } catch (err: any) {
      console.error('Exception in inventoryService.list:', err);
      return { data: [], error: err.message };
    }
  },

  /**
   * Get single inventory item by ID
   */
  async getById(id: string, organization_id: string): Promise<{ data: InventoryItem | null; error: any }> {
    try {
      const token = await getToken();
      const response = await fetch(`${API_BASE_URL}/${id}?organization_id=${organization_id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        return { data: null, error: errorData.error || 'Failed to fetch inventory item' };
      }

      const result = await response.json();
      return { data: result.data, error: null };
    } catch (err: any) {
      console.error('Exception in inventoryService.getById:', err);
      return { data: null, error: err.message };
    }
  },

  /**
   * Create new inventory item
   */
  async create(input: InventoryItemInput): Promise<{ data: InventoryItem | null; error: any }> {
    try {
      const token = await getToken();
      const response = await fetch(API_BASE_URL, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(input),
      });

      if (!response.ok) {
        const errorData = await response.json();
        return { data: null, error: errorData.error || 'Failed to create inventory item' };
      }

      const result = await response.json();
      return { data: result.data, error: null };
    } catch (err: any) {
      console.error('Exception in inventoryService.create:', err);
      return { data: null, error: err.message };
    }
  },

  /**
   * Update inventory item
   */
  async update(
    id: string,
    organization_id: string,
    updates: Partial<InventoryItemInput>
  ): Promise<{ data: InventoryItem | null; error: any }> {
    try {
      const token = await getToken();
      const response = await fetch(`${API_BASE_URL}/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ organization_id, ...updates }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        return { data: null, error: errorData.error || 'Failed to update inventory item' };
      }

      const result = await response.json();
      return { data: result.data, error: null };
    } catch (err: any) {
      console.error('Exception in inventoryService.update:', err);
      return { data: null, error: err.message };
    }
  },

  /**
   * Delete inventory item
   */
  async delete(id: string, organization_id: string): Promise<{ error: any }> {
    try {
      const token = await getToken();
      const response = await fetch(`${API_BASE_URL}/${id}?organization_id=${organization_id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        return { error: errorData.error || 'Failed to delete inventory item' };
      }

      return { error: null };
    } catch (err: any) {
      console.error('Exception in inventoryService.delete:', err);
      return { error: err.message };
    }
  },

  /**
   * Get low stock items
   */
  async getLowStockItems(organization_id: string): Promise<{ data: InventoryItem[]; error: any }> {
    try {
      const token = await getToken();
      const response = await fetch(`${API_BASE_URL}/low-stock?organization_id=${organization_id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        return { data: [], error: errorData.error || 'Failed to fetch low stock items' };
      }

      const result = await response.json();
      return { data: result.data || [], error: null };
    } catch (err: any) {
      console.error('Exception in inventoryService.getLowStockItems:', err);
      return { data: [], error: err.message };
    }
  },
};

// =====================================================
// INVENTORY TRANSACTIONS SERVICE
// =====================================================

export const transactionsService = {
  /**
   * List transactions with filters
   */
  async list(params: {
    organization_id: string;
    inventory_item_id?: string;
    transaction_type?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ data: InventoryTransaction[]; error: any }> {
    try {
      const token = await getToken();
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, String(value));
        }
      });

      const response = await fetch(`${API_BASE_URL}/transactions/list?${queryParams.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        return { data: [], error: errorData.error || 'Failed to fetch transactions' };
      }

      const result = await response.json();
      return { data: result.data || [], error: null };
    } catch (err: any) {
      console.error('Exception in transactionsService.list:', err);
      return { data: [], error: err.message };
    }
  },

  /**
   * Create new transaction (automatically updates stock)
   */
  async create(input: InventoryTransactionInput): Promise<{ data: InventoryTransaction | null; error: any }> {
    try {
      const token = await getToken();
      const response = await fetch(`${API_BASE_URL}/transactions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(input),
      });

      if (!response.ok) {
        const errorData = await response.json();
        return { data: null, error: errorData.error || 'Failed to create transaction' };
      }

      const result = await response.json();
      return { data: result.data, error: null };
    } catch (err: any) {
      console.error('Exception in transactionsService.create:', err);
      return { data: null, error: err.message };
    }
  },

  /**
   * Get transaction history for specific item
   */
  async getItemHistory(
    inventory_item_id: string,
    organization_id: string
  ): Promise<{ data: InventoryTransaction[]; error: any }> {
    try {
      const token = await getToken();
      const response = await fetch(
        `${API_BASE_URL}/transactions/history/${inventory_item_id}?organization_id=${organization_id}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        return { data: [], error: errorData.error || 'Failed to fetch transaction history' };
      }

      const result = await response.json();
      return { data: result.data || [], error: null };
    } catch (err: any) {
      console.error('Exception in transactionsService.getItemHistory:', err);
      return { data: [], error: err.message };
    }
  },
};

// =====================================================
// INVENTORY ALERTS SERVICE
// =====================================================

export const alertsService = {
  /**
   * Get active alerts for organization
   */
  async getActive(organization_id: string): Promise<{ data: InventoryAlert[]; error: any }> {
    try {
      const token = await getToken();
      const response = await fetch(`${API_BASE_URL}/alerts/active?organization_id=${organization_id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        return { data: [], error: errorData.error || 'Failed to fetch alerts' };
      }

      const result = await response.json();
      return { data: result.data || [], error: null };
    } catch (err: any) {
      console.error('Exception in alertsService.getActive:', err);
      return { data: [], error: err.message };
    }
  },

  /**
   * Acknowledge alert
   */
  async acknowledge(id: string, organization_id: string): Promise<{ error: any }> {
    try {
      const token = await getToken();
      const response = await fetch(`${API_BASE_URL}/alerts/${id}/acknowledge`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ organization_id }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        return { error: errorData.error || 'Failed to acknowledge alert' };
      }

      return { error: null };
    } catch (err: any) {
      console.error('Exception in alertsService.acknowledge:', err);
      return { error: err.message };
    }
  },

  /**
   * Acknowledge all alerts for an item
   */
  async acknowledgeAllForItem(inventory_item_id: string, organization_id: string): Promise<{ error: any }> {
    try {
      const token = await getToken();
      const response = await fetch(`${API_BASE_URL}/alerts/item/${inventory_item_id}/acknowledge-all`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ organization_id }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        return { error: errorData.error || 'Failed to acknowledge alerts' };
      }

      return { error: null };
    } catch (err: any) {
      console.error('Exception in alertsService.acknowledgeAllForItem:', err);
      return { error: err.message };
    }
  },
};

const inventoryCrmService = {
  inventory: inventoryService,
  transactions: transactionsService,
  alerts: alertsService,
};

export default inventoryCrmService;
